import './BetbookPostsDialog.less';

import clsx from 'clsx';
import React from 'react';
import { appDb, dispatch, useReframe } from 'nike-re-framejs';

import PopupNg from '@/ui/PopupNg';
import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import { DialogBody, Docked } from '@/ui/dialogs/Dialog';
import RadioButton from '@/common/components/RadioButton';
import BetbookExtendedFilter from 'nike-ui/src/components/betbook/betbookFilters/BetbookExtendedFilter';
import {
    loadBetbookPosts,
    removeTopEventsDateFilter,
    removeTopEventsSportFilter,
    setTopEventsDateFilter,
    setTopEventsSportFilter
} from '@/betbook/handlers';
import { COMMUNITY_ANALYSES, COMMUNITY_POSTS_AUTHOR } from '@/routing/routes';
import { filterByDate$, filterNickname$, filterPostType$, filterSorting$ } from '@/betbook/subs';
import { topEventsFilterByDate$, topEventsFilterBySport$ } from 'nike-ui/src/components/betbook/subs';
import {
    POST_ANALYSIS,
    POST_BLOG,
    POST_TICKET,
    SORTING_COMMENTS,
    SORTING_INSPIRATIONS,
    SORTING_NEW,
    SORTING_RECOMMENDED,
    SORTING_TOP,
    SORTING_VIEWS,
    TIME_TODAY,
    TIME_TOMORROW,
    timeEnum2Url,
    toBetbookAuthorPathParams,
    toBetbookQueryParams
} from 'nike-ui/src/components/betbook/constants';
import { SportIconNui } from 'nike-ui/src/icons/SportIconNui';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

function BetbookPostsDialog({ dialogOpenKey, postType }) {
    function onFilter() {
        setTimeout(() => {
            PopupNg.dispatcher.close();
        }, 200);
    }
    return (
        <Docked onClose={onFilter}>
            <DialogBody className={clsx({ 'betbook-filter-dialog': dialogOpenKey === 'extendedFilterDialog' })}>
                {dialogOpenKey === 'postTypeDialog' ? (
                    <BetbookPostsDropdown sectionTitle={i18n('betbook.dropdown.title.posts')} />
                ) : dialogOpenKey === 'sortingDialog' ? (
                    <BetbookSortDropdown postType={postType} sectionTitle={i18n('betbook.dropdown.title.sort')} />
                ) : dialogOpenKey === 'extendedFilterDialog' ? (
                    <BetbookExtendedFilterDropdown sectionTitle={i18n('betbook.dropdown.title.extended-filter')} />
                ) : dialogOpenKey === 'topEventsBySportDialog' ? (
                    <BetbookTopEventsSportDropdown sectionTitle={i18n('betbook.topEvents.dropdown.title.sport')} />
                ) : dialogOpenKey === 'topEventsByDateDialog' ? (
                    <BetbookTopEventsTimeDropdown sectionTitle={i18n('betbook.topEvents.dropdown.title.date')} />
                ) : null}
            </DialogBody>
        </Docked>
    );
}

function currentRoute() {
    return appDb.peekValue().getIn(['route']);
}

function BetbookPostsDropdown({ sectionTitle, sliderClassName }) {
    const { subscribe } = useReframe();
    const activeType = subscribe(filterPostType$);
    const nickname = subscribe(filterNickname$);

    function setNavigate(e) {
        const type = e.currentTarget.getAttribute('value');
        if (!type && !activeType) {
            PopupNg.dispatcher.close();
            return;
        }
        navigate(COMMUNITY_POSTS_AUTHOR, toBetbookAuthorPathParams(nickname, type));
        PopupNg.dispatcher.close();
    }

    return (
        <div className="betbook-filter-section">
            <div className="section-title">{sectionTitle}</div>
            <div className={clsx('buttons-options', sliderClassName)}>
                <RadioButton
                    active={activeType === POST_ANALYSIS || !activeType}
                    children={i18n('betbook.dropdown.ANALYSIS')}
                    data-atid="betbook-filter-post-analysis"
                    onClick={setNavigate}
                    value={POST_ANALYSIS}
                />
                <RadioButton
                    active={activeType === POST_TICKET}
                    children={i18n('betbook.dropdown.TICKET')}
                    data-atid="betbook-filter-post-ticket"
                    onClick={setNavigate}
                    value={POST_TICKET}
                />
            </div>
        </div>
    );
}

function BetbookSortDropdown({ postType, sectionTitle, sliderClassName }) {
    const { subscribe } = useReframe();
    const sorting = subscribe(filterSorting$);
    const activeType = subscribe(filterPostType$);
    const nickname = subscribe(filterNickname$);
    const byDate = subscribe(filterByDate$);
    const route = currentRoute();

    function setNavigate(e) {
        const newSortType = e.currentTarget.getAttribute('value');
        navigate(
            currentRoute(),
            toBetbookAuthorPathParams(nickname, postType),
            toBetbookQueryParams({ sortType: newSortType, byDate })
        );
        PopupNg.dispatcher.close();
    }

    return (
        <div className="betbook-filter-section">
            <div className="section-title">{sectionTitle}</div>
            <div className={clsx('buttons-options', sliderClassName)}>
                {route === COMMUNITY_ANALYSES ? (
                    <RadioButton
                        active={!sorting || sorting === SORTING_RECOMMENDED}
                        children={i18n('betbook.sorting.RECOMMENDED')}
                        data-atid="betbook-filter-sorting-recommended"
                        onClick={setNavigate}
                        value={SORTING_RECOMMENDED}
                    />
                ) : null}
                <RadioButton
                    active={(postType !== POST_ANALYSIS && !sorting) || sorting === SORTING_NEW}
                    children={i18n('betbook.sorting.NEWEST')}
                    data-atid="betbook-filter-sorting-new"
                    onClick={setNavigate}
                    value={SORTING_NEW}
                />
                <RadioButton
                    active={sorting === SORTING_TOP}
                    children={i18n('betbook.dropdown.TOP')}
                    data-atid="betbook-filter-sorting-top"
                    onClick={setNavigate}
                    value={SORTING_TOP}
                />
                {route === COMMUNITY_POSTS_AUTHOR || activeType === POST_BLOG ? null : (
                    <RadioButton
                        active={sorting === SORTING_INSPIRATIONS}
                        children={i18n('betbook.dropdown.INSPIRATIONS')}
                        data-atid="betbook-filter-sorting-inspirations"
                        onClick={setNavigate}
                        value={SORTING_INSPIRATIONS}
                    />
                )}
                <RadioButton
                    active={sorting === SORTING_VIEWS}
                    children={i18n('betbook.dropdown.VIEWS')}
                    data-atid="betbook-filter-sorting-views"
                    onClick={setNavigate}
                    value={SORTING_VIEWS}
                />
                <RadioButton
                    active={sorting === SORTING_COMMENTS}
                    children={i18n('betbook.dropdown.COMMENTS')}
                    data-atid="betbook-filter-sorting-comments"
                    onClick={setNavigate}
                    value={SORTING_COMMENTS}
                />
            </div>
        </div>
    );
}

function BetbookExtendedFilterDropdown({ sectionTitle }) {
    const { subscribe } = useReframe();
    const postType = subscribe(filterPostType$);
    const sorting = subscribe(filterSorting$);

    function onFilterSubmit(params) {
        const { expirationTag, sportIds, odds, stake, win, betCount } = params || {};
        dispatch(loadBetbookPosts(postType, sorting, sportIds, odds, stake, win, betCount, expirationTag));
        PopupNg.dispatcher.close();
    }
    function onFilterReset() {
        onFilterSubmit();
    }
    return (
        <div className="betbook-filter-section">
            <div className="section-title">{sectionTitle}</div>
            <BetbookExtendedFilter
                className="mb-30"
                onReset={onFilterReset}
                onSubmit={onFilterSubmit}
                permanentlyVisibleSports={5}
                postType={postType}
                stickyFooter
                twoColumnSportsFilter
            />
        </div>
    );
}

function BetbookTopEventsTimeDropdown({ sectionTitle, sliderClassName }) {
    const { subscribe } = useReframe();
    const topEventsDateFilter = subscribe(topEventsFilterByDate$);
    const topEventsSportFilter = subscribe(topEventsFilterBySport$);

    function filterChangeHandler(e) {
        const time = e.currentTarget.getAttribute('value');
        time ? dispatch(setTopEventsDateFilter(time)) : dispatch(removeTopEventsDateFilter());
        navigate(currentRoute(), {
            sport: getSportNameById(topEventsSportFilter, false),
            time: time ? timeEnum2Url[time] : null
        });
        PopupNg.dispatcher.close();
    }

    return (
        <div className="betbook-filter-section">
            <div className="section-title">{sectionTitle}</div>
            <div className={clsx('buttons-options', sliderClassName)}>
                <RadioButton
                    active={!topEventsDateFilter}
                    children={i18n('betbook.dropdown.ANYTIME')}
                    data-atid="betbook-filter-anytime"
                    onClick={filterChangeHandler}
                    value={null}
                />
                <RadioButton
                    active={topEventsDateFilter === TIME_TODAY}
                    children={i18n('betbook.dropdown.TODAY')}
                    data-atid="betbook-filter-today"
                    onClick={filterChangeHandler}
                    value={TIME_TODAY}
                />
                <RadioButton
                    active={topEventsDateFilter === TIME_TOMORROW}
                    children={i18n('betbook.dropdown.TOMORROW')}
                    data-atid="betbook-filter-tomorrow"
                    onClick={filterChangeHandler}
                    value={TIME_TOMORROW}
                />
            </div>
        </div>
    );
}

function BetbookTopEventsSportDropdown({ sectionTitle, sliderClassName }) {
    const { subscribe } = useReframe();
    const topEventsSportFilter = parseInt(subscribe(topEventsFilterBySport$));
    const topEventsDateFilter = subscribe(topEventsFilterByDate$);

    function returnSportIconAndName(sportId) {
        return (
            <>
                <SportIconNui sportId={sportId} className={'mr-10'} />
                <span className="capitalize-first">{getSportNameById(sportId, false)}</span>
            </>
        );
    }

    function filterChangeHandler(e) {
        const sportId = e.currentTarget.getAttribute('value');
        sportId ? dispatch(setTopEventsSportFilter(sportId)) : dispatch(removeTopEventsSportFilter());
        navigate(currentRoute(), {
            sport: sportId ? getSportNameById(sportId, false) : null,
            time: timeEnum2Url[topEventsDateFilter]
        });
        PopupNg.dispatcher.close();
    }

    return (
        <div className="betbook-filter-section">
            <div className="section-title">{sectionTitle}</div>
            <div className={clsx('buttons-options', sliderClassName)}>
                <RadioButton
                    active={!topEventsSportFilter}
                    children={i18n('betbook.topEvents.allSports')}
                    onClick={filterChangeHandler}
                    data-value={null}
                />
                <RadioButton
                    children={returnSportIconAndName(1)}
                    active={topEventsSportFilter === 1}
                    onClick={filterChangeHandler}
                    value={1}
                />
                <RadioButton
                    children={returnSportIconAndName(3)}
                    active={topEventsSportFilter === 3}
                    onClick={filterChangeHandler}
                    value={3}
                />
                <RadioButton
                    children={returnSportIconAndName(7)}
                    active={topEventsSportFilter === 7}
                    onClick={filterChangeHandler}
                    value={7}
                />
                <RadioButton
                    children={returnSportIconAndName(5)}
                    active={topEventsSportFilter === 5}
                    onClick={filterChangeHandler}
                    value={5}
                />
                <RadioButton
                    children={returnSportIconAndName(11)}
                    active={topEventsSportFilter === 11}
                    onClick={filterChangeHandler}
                    value={11}
                />
            </div>
        </div>
    );
}

export default BetbookPostsDialog;
